import { Formik } from 'formik';
import * as yup from 'yup';
import {
  useTheme,
  useMediaQuery,
  Box,
  Typography,
  Button,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
} from '@mui/material';
import { ContentCopy } from '@mui/icons-material';


const schema = yup.object({
  seasonCode: yup.string().required('Required'),
  seasonName: yup.string().required('Required'),
  startDate: yup.string().required('Required'),
  endDate: yup.string().required('Required'),
});

const EMPTY = { seasonCode: '', seasonName: '', startDate: '', endDate: '' };


const DuplicateSeasonDialog = ({ open, onClose, sourceCode, onDuplicate }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth fullScreen={isMobile}>
      <DialogTitle>
        <Box display="flex" alignItems="center" gap={1}>
          <ContentCopy fontSize="small" color="primary" />
          <Box>
            <Typography variant="subtitle1" fontWeight="bold" lineHeight={1.2}>
              Duplicate Season
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Copy from: {sourceCode}
            </Typography>
          </Box>
        </Box>
      </DialogTitle>

      <Formik 
        initialValues={EMPTY} 
        validationSchema={schema} 
        onSubmit={onDuplicate} 
        enableReinitialize
      >
        {({ 
          values, 
          errors, 
          touched, 
          handleChange, 
          handleBlur, 
          handleSubmit, 
          isSubmitting 
        }) => (
          <form 
            onSubmit={handleSubmit} 
            style={{ 
              display: 'flex', 
              flexDirection: 'column', 
              flex: 1, 
              overflow: 'hidden' 
            }}
          >
            <DialogContent dividers>

              {/* Source info banner */}
              <Box
                display="flex" alignItems="center" gap={2}
                sx={{
                  p: 1.5, borderRadius: 1.5,
                  bgcolor: 'action.hover',
                  border: '1px solid', borderColor: 'divider',
                }}
              >
                <Box sx={{ width: 4, alignSelf: 'stretch', bgcolor: 'warning.main', borderRadius: 1, flexShrink: 0 }} />
                <Box>
                  <Typography variant="caption" color="text.secondary">
                    Source season
                  </Typography>
                  <Typography variant="body1" fontWeight="bold">
                    {sourceCode}
                  </Typography>
                </Box>
              </Box>

              <Typography variant="body2" color="text.secondary" my={2}>
                All scheduled flight patterns from this season will be copied into the new season.
              </Typography>

              <Box 
                display="grid" 
                gridTemplateColumns={{ xs: '1fr', sm: '1fr 1fr' }} 
                gap={2}
              >
                <TextField
                  variant="filled" 
                  fullWidth 
                  label="New Season Code *" 
                  name="seasonCode"
                  value={values.seasonCode} 
                  onChange={handleChange} 
                  onBlur={handleBlur}
                  error={touched.seasonCode && Boolean(errors.seasonCode)}
                  helperText={touched.seasonCode && errors.seasonCode}
                  slotProps={{ htmlInput: { style: { textTransform: 'uppercase' } } }}
                />

                <TextField
                  variant="filled" 
                  fullWidth 
                  label="Season Name *" 
                  name="seasonName"
                  value={values.seasonName} 
                  onChange={handleChange} 
                  onBlur={handleBlur}
                  error={touched.seasonName && Boolean(errors.seasonName)}
                  helperText={touched.seasonName && errors.seasonName}
                />

                <TextField
                  variant="filled" 
                  fullWidth 
                  label="Start Date *" 
                  name="startDate" 
                  type="date"
                  value={values.startDate} 
                  onChange={handleChange} 
                  onBlur={handleBlur}
                  slotProps={{ inputLabel: { shrink: true } }}
                  error={touched.startDate && Boolean(errors.startDate)}
                  helperText={touched.startDate && errors.startDate}
                />

                <TextField
                  variant="filled" 
                  fullWidth 
                  label="End Date *" 
                  name="endDate" 
                  type="date"
                  value={values.endDate} 
                  onChange={handleChange} 
                  onBlur={handleBlur}
                  slotProps={{ inputLabel: { shrink: true } }}
                  error={touched.endDate && Boolean(errors.endDate)}
                  helperText={touched.endDate && errors.endDate}
                />

              </Box>
            </DialogContent>

            <DialogActions>
              <Button
                fullWidth={isMobile}
                variant="outlined"
                color="secondary"
                disabled={isSubmitting}
                onClick={onClose}
              >
                Cancel
              </Button>
              <Button
                fullWidth={isMobile}
                type="submit"
                variant="outlined"
                disabled={isSubmitting}
                startIcon={isSubmitting ? <CircularProgress size={16} color="inherit" /> : <ContentCopy />}
              >
                {isSubmitting ? 'Duplicating…' : 'Duplicate'}
              </Button>
            </DialogActions>
          </form>
        )}
      </Formik>
    </Dialog>
  );
};

export default DuplicateSeasonDialog;
